import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type PropsWithChildren,
} from 'react';
import type { Verse } from '@quranjs/api';
import { groupBy } from 'lodash';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Menu } from '@/features/navigation/components';
import { useSession } from '@/features/auth/hooks/use-session';
import { PageRating } from '@/features/memorization/components/page-rating';
import { RecallOverlay } from '@/features/memorization/components/recall-overlay';
import { useCard } from '@/features/memorization/hooks/use-card';
import { useDueCards } from '@/features/memorization/hooks/use-due-cards';
import { useRecallSession } from '@/features/memorization/hooks/use-recall-session';
import { useReviewMode } from '@/features/memorization/hooks/use-review-mode';
import { PageBreadcrumb } from '@/features/reader/components/page-breadcrumb';
import { PageReaderLines } from '@/features/reader/components/page-lines';
import {
  PageReaderContextProvider,
  type PageReaderContextType,
  usePageReaderContext,
} from '@/features/reader/hooks/use-page-reader-context';
import {
  ChromeStoreContext,
  createChromeStore,
  useChromeStoreContext,
} from '@/features/reader/stores/chrome-visibility';
import { useLayoutMode } from '@/features/reader/hooks/use-layout-mode';
import { usePageKeyboard } from '@/features/reader/hooks/use-page-keyboard';
import { fetchVersesByPage } from '@/features/reader/lib/queries';
import { READER_PAGES_COUNT } from '@/shared/constants/constants';
import { cn } from '@/shared/lib/utils';

// --- Rating focus context for spread mode ---
const RatingFocusContext = createContext<string | null>(null);

export function useRatingFocus() {
  return useContext(RatingFocusContext);
}

function PageReaderLayout({ children }: PropsWithChildren) {
  const { pageId } = usePageReaderContext();
  const pageNumber = Number(pageId);
  const navigate = useNavigate();
  const { layoutMode } = useLayoutMode();
  const isSpread = layoutMode === 'spread';

  // Compute spread page numbers (same logic as SpreadPageContent)
  const spreadStart = pageNumber % 2 === 1 ? pageNumber : pageNumber - 1;
  const spreadEnd = spreadStart + 1;
  const hasSecondPage = isSpread && spreadEnd <= READER_PAGES_COUNT;

  // In single mode, always rate the page from the URL; in spread, rate both pages
  const rightPageNum = isSpread ? spreadStart : pageNumber;
  const leftPageNum = spreadEnd;

  const { isAuthenticated } = useSession();

  // Card for the right page (or the only page in single mode) — always active
  const rightCard = useCard(rightPageNum, isAuthenticated);
  // Card for the left page — only active in spread mode with a valid second page
  const leftCard = useCard(leftPageNum, isAuthenticated, hasSecondPage);

  const { dueCards } = useDueCards(isAuthenticated);
  const { isReviewMode } = useReviewMode();

  // "Needs rating" = new page (no card yet) OR card is due for review
  // "Is due" = card exists and its due date has passed (triggers recall overlay)
  const cardIsDue = (c: { isLoading: boolean; card: { due?: Date } | null }) =>
    !c.isLoading && c.card?.due ? new Date(c.card.due) <= new Date() : false;
  const cardNeedsRating = (c: {
    isLoading: boolean;
    card: { due?: Date } | null;
  }) => !c.isLoading && (c.card == null || cardIsDue(c));

  const rightNeedsRating = cardNeedsRating(rightCard);
  const leftNeedsRating = hasSecondPage && cardNeedsRating(leftCard);
  const rightIsDue = cardIsDue(rightCard);
  const leftIsDue = hasSecondPage && cardIsDue(leftCard);

  // Exclude both spread pages from remaining due count
  const spreadPages = hasSecondPage ? [spreadStart, spreadEnd] : [pageNumber];
  const remainingDue = dueCards.filter(
    (c) => !spreadPages.includes(c.pageNumber),
  );

  // "All rated" = every page that needs rating has been rated
  const allRated = hasSecondPage
    ? (!rightNeedsRating || rightCard.rated) &&
      (!leftNeedsRating || leftCard.rated)
    : rightCard.rated;

  // Focus only pages that need rating, only when review mode is active
  const focusedPageId: string | null =
    hasSecondPage && isReviewMode && isAuthenticated
      ? rightNeedsRating && !rightCard.rated
        ? String(spreadStart)
        : leftNeedsRating && !leftCard.rated
          ? String(spreadEnd)
          : null
      : null;

  // Select the active card based on focus (or the only card in single mode)
  const activeCard = focusedPageId === String(spreadEnd) ? leftCard : rightCard;

  // Recall overlay only triggers for cards that are actually due (not new pages)
  const anyDue = rightIsDue || leftIsDue;

  const { isContentVisible, isRatingVisible, reveal } = useRecallSession({
    isReviewMode,
    isDue: anyDue && !allRated,
    isAuthenticated,
  });

  const goToNextDuePage = () => {
    if (remainingDue.length > 0) {
      void navigate({
        to: '/page/$id',
        params: { id: String(remainingDue[0].pageNumber) },
      });
    }
  };

  const chromeStore = useChromeStoreContext();

  const step = isSpread ? 2 : 1;

  const navigatePage = useCallback(
    (id: string, direction: 'left' | 'right') => {
      void navigate({
        to: '/page/$id',
        params: { id },
        viewTransition: { types: [`slide-${direction}`] },
      });
    },
    [navigate],
  );

  usePageKeyboard({
    onNavigate: navigatePage,
    currentPageNumber: pageNumber,
    step,
  });

  // Page label shown only in spread mode while individual pages are being rated
  const focusedPage =
    hasSecondPage && focusedPageId != null ? focusedPageId : undefined;

  // Reset handler: reset the active card (or right card when all are rated)
  const resetRated = allRated
    ? () => {
        rightCard.resetRated();
        leftCard.resetRated();
      }
    : activeCard.resetRated;

  // Pin chrome only while there are unrated pages that need attention
  const anyNeedsRating = rightNeedsRating || leftNeedsRating;
  const ratingNeedsAttention = isRatingVisible && anyNeedsRating && !allRated;

  useEffect(() => {
    const s = chromeStore.getState();
    if (ratingNeedsAttention) s.pin('rating');
    else s.unpin('rating');
    if (!isContentVisible) s.pin('recall');
    else s.unpin('recall');
  }, [ratingNeedsAttention, isContentVisible, chromeStore]);

  const containerRefCallback = useCallback(
    (el: HTMLDivElement | null) => chromeStore.getState().setContainer(el),
    [chromeStore],
  );

  const handleContentTap = useCallback(() => {
    if (window.getSelection()?.toString()) return;
    chromeStore.getState().toggle();
  }, [chromeStore]);

  return (
    <RatingFocusContext value={focusedPageId}>
      <div
        ref={containerRefCallback}
        className={cn('group/chrome relative h-dvh mx-auto overflow-hidden')}
        data-testid={`page-${pageId}-container`}
      >
        <div className="flex flex-col h-full overflow-hidden gap-2">
          <PageBreadcrumb />
          <main
            className="relative flex justify-center overflow-auto flex-1 pb-20 [view-transition-name:page-content]"
            onClick={handleContentTap}
          >
            <div
              className={cn(
                'my-auto transition-[filter] duration-300',
                !isContentVisible && 'blur-lg pointer-events-none select-none',
              )}
            >
              {children}
            </div>
            {!isContentVisible && (
              <RecallOverlay pageNumber={pageNumber} onReveal={reveal} />
            )}
            {/* Edge click zones — pointer devices only */}
            <button
              type="button"
              aria-label="Previous page"
              className="group/zone absolute inset-y-0 right-0 w-[18%] cursor-pointer hidden [@media(hover:hover)]:flex items-center justify-center"
              onClick={(e) => {
                e.stopPropagation();
                if (pageNumber - step >= 1) {
                  navigatePage(String(pageNumber - step), 'left');
                }
              }}
            >
              <ChevronRight className="size-6 text-foreground opacity-0 transition-opacity duration-200 group-hover/zone:opacity-15" />
            </button>
            <button
              type="button"
              aria-label="Next page"
              className="group/zone absolute inset-y-0 left-0 w-[18%] cursor-pointer hidden [@media(hover:hover)]:flex items-center justify-center"
              onClick={(e) => {
                e.stopPropagation();
                if (pageNumber + step <= READER_PAGES_COUNT) {
                  navigatePage(String(pageNumber + step), 'right');
                }
              }}
            >
              <ChevronLeft className="size-6 text-foreground opacity-0 transition-opacity duration-200 group-hover/zone:opacity-15" />
            </button>
          </main>
        </div>
        <Menu
          ratingRow={
            isRatingVisible ? (
              <PageRating
                intervals={activeCard.intervals}
                rate={activeCard.rate}
                isRating={activeCard.isRating}
                rated={allRated}
                resetRated={resetRated}
                remainingDueCount={remainingDue.length}
                onNextDuePage={goToNextDuePage}
                focusedPage={focusedPage}
              />
            ) : undefined
          }
        />
      </div>
    </RatingFocusContext>
  );
}

function SpreadPageContent({
  primaryData,
}: {
  primaryData: PageReaderContextType;
}) {
  const focusedPageId = useRatingFocus();
  const pageNumber = Number(primaryData.pageId);
  const spreadStart = pageNumber % 2 === 1 ? pageNumber : pageNumber - 1;
  const spreadEnd = spreadStart + 1;
  const hasSecondPage = spreadEnd <= READER_PAGES_COUNT;

  // Determine which page is the primary (from route loader) and which needs fetching
  const needsFetch = hasSecondPage && spreadStart !== pageNumber;
  const fetchPageId = needsFetch
    ? String(spreadStart)
    : hasSecondPage
      ? String(spreadEnd)
      : null;

  const { data: fetchedVerses } = useQuery({
    queryKey: ['verses-by-page', fetchPageId],
    queryFn: () => fetchVersesByPage(fetchPageId!),
    enabled: fetchPageId != null,
    staleTime: Number.POSITIVE_INFINITY,
  });

  // Build data for each page in the spread
  const rightPageId = String(spreadStart); // odd = right page
  const leftPageId = String(spreadEnd); // even = left page

  const rightData: PageReaderContextType =
    rightPageId === primaryData.pageId
      ? primaryData
      : {
          ...primaryData,
          pageId: rightPageId,
          versesByChapter: fetchedVerses
            ? groupBy(fetchedVerses, (v: Verse) => v.chapterId)
            : {},
        };

  const leftData: PageReaderContextType =
    leftPageId === primaryData.pageId
      ? primaryData
      : {
          ...primaryData,
          pageId: leftPageId,
          versesByChapter: fetchedVerses
            ? groupBy(fetchedVerses, (v: Verse) => v.chapterId)
            : {},
        };

  const rightReady = Object.keys(rightData.versesByChapter).length > 0;
  const leftReady =
    !hasSecondPage || Object.keys(leftData.versesByChapter).length > 0;

  return (
    <div className="flex flex-row-reverse w-full h-full gap-2 px-2">
      {/* Right page (odd / spreadStart) — content aligns to inner edge */}
      <div
        className={cn(
          'flex-1 flex items-center justify-start min-w-0 pl-4 rounded-2xl transition-shadow',
          focusedPageId === rightPageId &&
            'shadow-[inset_0_0_0_2px_oklch(var(--primary)/0.2)]',
        )}
        style={{ fontFamily: `page${rightPageId}` }}
      >
        {rightReady && (
          <PageReaderContextProvider value={rightData}>
            <PageReaderLines />
          </PageReaderContextProvider>
        )}
      </div>
      {/* Spine divider */}
      <div className="w-px bg-border shrink-0" />
      {/* Left page (even / spreadEnd) — content aligns to inner edge */}
      {hasSecondPage && (
        <div
          className={cn(
            'flex-1 flex items-center justify-end min-w-0 pr-4 rounded-2xl transition-shadow',
            focusedPageId === leftPageId &&
              'shadow-[inset_0_0_0_2px_oklch(var(--primary)/0.2)]',
          )}
          style={{ fontFamily: `page${leftPageId}` }}
        >
          {leftReady && (
            <PageReaderContextProvider value={leftData}>
              <PageReaderLines />
            </PageReaderContextProvider>
          )}
        </div>
      )}
    </div>
  );
}

export default function PageReader(pageRenderData: PageReaderContextType) {
  const { layoutMode } = useLayoutMode();
  const isSpread = layoutMode === 'spread';

  // Chrome store lives above the key so visibility persists across page navigations.
  const chromeStore = useMemo(() => createChromeStore(), []);

  useEffect(() => {
    return () => chromeStore.getState().dispose();
  }, [chromeStore]);

  if (isSpread) {
    return (
      <ChromeStoreContext value={chromeStore}>
        <PageReaderContextProvider value={pageRenderData}>
          <PageReaderLayout key={pageRenderData.pageId}>
            <SpreadPageContent primaryData={pageRenderData} />
          </PageReaderLayout>
        </PageReaderContextProvider>
      </ChromeStoreContext>
    );
  }

  return (
    <ChromeStoreContext value={chromeStore}>
      <PageReaderContextProvider value={pageRenderData}>
        <PageReaderLayout key={pageRenderData.pageId}>
          <div style={{ fontFamily: `page${pageRenderData.pageId}` }}>
            <PageReaderLines />
          </div>
        </PageReaderLayout>
      </PageReaderContextProvider>
    </ChromeStoreContext>
  );
}
